import { useEffect, useMemo, useState } from 'react';
import { DEFAULT_ROLE_FILTER, formatRoleLabel } from '../constants/entityUsers';
import UserActions from './UserActions';
import FeedbackState, { LoadingTable } from './FeedbackState';
import StatusChip from './StatusChip';
import { formatAdminDate } from './adminPresentation';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const EntityUsersSection = ({
  users = [],
  availableRoles = [],
  selectedRoles = [],
  onRoleToggle,
  searchTerm,
  onSearchChange,
  status = 'active',
  onStatusChange,
  isLoading,
  page = 1,
  limit = 25,
  totalItems = 0,
  totalPages = 1,
  onPageChange,
  pageSize = 25,
  onPageSizeChange,
  busyUserIds = {},
  onViewProfile,
  onToggleEnabled,
  onArchive,
}) => {
  const [sortBy, setSortBy] = useState('recent');
  const [jumpPage, setJumpPage] = useState('');

  useEffect(() => {
    setSortBy('recent');
  }, [users.length]);

  useEffect(() => {
    setJumpPage(String(page || 1));
  }, [page]);

  const sortedUsers = useMemo(() => {
    const list = [...users];

    if (sortBy === 'name') {
      return list.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    }

    if (sortBy === 'role') {
      return list.sort((a, b) => {
        const roleA = (a.roles || [])[0] || '';
        const roleB = (b.roles || [])[0] || '';
        return roleA.localeCompare(roleB);
      });
    }

    return list.sort((a, b) => new Date(b.joinedAt || 0) - new Date(a.joinedAt || 0));
  }, [sortBy, users]);

  const roleOptions = useMemo(
    () => [DEFAULT_ROLE_FILTER, ...availableRoles.filter(Boolean)],
    [availableRoles],
  );

  const start = totalItems === 0 ? 0 : ((page - 1) * limit) + 1;
  const end = totalItems === 0 ? 0 : Math.min(page * limit, totalItems);

  return (
    <div className="d-grid gap-3">
      <div>
        <p className="text-muted small mb-1">Users</p>
        <h3 className="h5 mb-1">Users in this entity</h3>
      </div>

      <div className="sa-resource-filters">
          <div className="d-flex flex-wrap gap-2">
            <button
              type="button"
              className={`btn btn-sm ${status === 'active' ? 'btn-primary' : 'btn-outline-secondary'}`}
              onClick={() => onStatusChange?.('active')}
            >
              Active
            </button>
            <button
              type="button"
              className={`btn btn-sm ${status === 'archived' ? 'btn-primary' : 'btn-outline-secondary'}`}
              onClick={() => onStatusChange?.('archived')}
            >
              Archived
            </button>
          </div>

          <div className="d-flex flex-column flex-lg-row gap-2">
            <input
              type="search"
              className="form-control"
              placeholder="Search name, email, or phone"
              value={searchTerm}
              onChange={(event) => onSearchChange(event.target.value)}
            />
            <select
              className="form-select"
              value={sortBy}
              onChange={(event) => setSortBy(event.target.value)}
              style={{ maxWidth: 240 }}
            >
              <option value="recent">Recently added</option>
              <option value="name">Name</option>
              <option value="role">Role</option>
            </select>
          </div>

          <div className="d-flex flex-wrap gap-2">
            {roleOptions.map((role) => {
              const isAll = role === DEFAULT_ROLE_FILTER;
              const isActive = isAll
                ? selectedRoles.length === 0
                : selectedRoles.includes(role);

              return (
                <button
                  key={role}
                  type="button"
                  className={`btn btn-sm ${isActive ? 'btn-primary' : 'btn-outline-secondary'}`}
                  onClick={() => onRoleToggle(role)}
                >
                  {isAll ? 'All' : formatRoleLabel(role)}
                </button>
              );
            })}
          </div>
      </div>

      {isLoading ? (
        <LoadingTable />
      ) : sortedUsers.length === 0 ? (
        <FeedbackState title="No matching users" message="Clear filters or search with a different name, email, or phone number." />
      ) : (
        <>
          <div className="table-responsive">
            <table className="table table-sm align-middle mb-0">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Contact</th>
                  <th>Role(s)</th>
                  <th>Status</th>
                  <th>Joined</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedUsers.map((user) => (
                  <tr key={user._id || user.id}>
                    <td className="fw-semibold">{user.name || '—'}</td>
                    <td>
                      <div>{user.email || '—'}</div>
                      {user.phone && <div className="text-muted small">{user.phone}</div>}
                    </td>
                    <td>
                      <div className="d-flex flex-wrap gap-1">
                        {(user.roles || []).map((role) => (
                          <span key={`${user._id}-${role}`} className="badge rounded-pill text-bg-light border">
                            {formatRoleLabel(role)}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td>
                      <div className="d-flex flex-column gap-1">
                        <StatusChip value={user.status || 'Pending'} />
                        <StatusChip value={user.enabled === false ? 'Disabled' : 'Active'} />
                      </div>
                    </td>
                    <td>{formatAdminDate(user.joinedAt)}</td>
                    <td>
                      <UserActions
                        user={user}
                        isBusy={Boolean(busyUserIds[user._id || user.id])}
                        onViewProfile={onViewProfile}
                        onToggleEnabled={onToggleEnabled}
                        onArchive={onArchive}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-2">
            <div className="text-muted small">Showing {start}–{end} of {totalItems}</div>

            <div className="d-flex flex-wrap align-items-center gap-2">
              <button type="button" className="btn btn-sm btn-outline-secondary" disabled={page <= 1} onClick={() => onPageChange(page - 1)}>
                Prev
              </button>
              <div className="small">Page {page} of {totalPages}</div>
              <button type="button" className="btn btn-sm btn-outline-secondary" disabled={page >= totalPages} onClick={() => onPageChange(page + 1)}>
                Next
              </button>
              <div className="d-flex align-items-center gap-1">
                <span className="small">Jump to</span>
                <input
                  type="number"
                  min="1"
                  max={totalPages}
                  className="form-control form-control-sm"
                  style={{ width: 80 }}
                  value={jumpPage}
                  onChange={(event) => setJumpPage(event.target.value)}
                  onBlur={() => {
                    const next = Number.parseInt(jumpPage, 10);
                    if (!Number.isNaN(next) && next >= 1 && next <= totalPages && next !== page) {
                      onPageChange(next);
                    }
                  }}
                />
              </div>
              <select
                className="form-select form-select-sm"
                style={{ width: 110 }}
                value={pageSize}
                onChange={(event) => onPageSizeChange(Number(event.target.value))}
              >
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <option key={size} value={size}>{size} / page</option>
                ))}
              </select>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default EntityUsersSection;
